import { useSelector } from 'react-redux';

import { get } from 'lodash';
import { TAppointment } from 'ui-model';

import { Setting } from '@lib/resources/types';
import { selectUserSettings } from '@lib/selectors';

import { TChecklistAppointment } from 'screens/ChecklistScreen/types';

const PRIORITY_ARRAY = ['appointmentTypes', 'facilities'];

function identifyResourceSettingsKeyToOverride(
  settingsToOverride: Setting['settingsToOverrideFrom'],
  settingKey: string,
) {
  if (!settingsToOverride) return undefined;
  const resourceKey = PRIORITY_ARRAY.find((resource) => settingsToOverride[resource]?.includes(settingKey));
  return resourceKey;
}

function getReminderSettings(settings: Setting, item: TChecklistAppointment | TAppointment) {
  const repliesResourceKey = identifyResourceSettingsKeyToOverride(settings.settingsToOverrideFrom, 'reminder.replies');
  const allowSelfRescheduleResourceKey = identifyResourceSettingsKeyToOverride(
    settings.settingsToOverrideFrom,
    'reminder.allowSelfReschedule',
  );
  let reminderSettings = settings.reminder || {};

  if (repliesResourceKey === 'appointmentTypes' && item.type?.settings) {
    reminderSettings = { ...reminderSettings, replies: get(item.type.settings, 'reminder.replies') };
  }

  if (repliesResourceKey === 'facilities' && item.facility?.settings) {
    reminderSettings = { ...reminderSettings, replies: get(item.facility.settings, 'reminder.replies') };
  }

  if (allowSelfRescheduleResourceKey === 'appointmentTypes' && item.type?.settings) {
    reminderSettings = {
      ...reminderSettings,
      allowSelfReschedule: get(item.type.settings, 'reminder.allowSelfReschedule'),
    };
  }

  if (allowSelfRescheduleResourceKey === 'facilities' && item.facility?.settings) {
    reminderSettings = {
      ...reminderSettings,
      allowSelfReschedule: get(item.facility.settings, 'reminder.allowSelfReschedule'),
    };
  }

  return reminderSettings;
}

export default function useAppointmentsActionsOptions(item: TChecklistAppointment | TAppointment) {
  const appointmentStatus = item.status;
  const settings = useSelector(selectUserSettings);
  const settingsReminder = getReminderSettings(settings, item);

  const canConfirmAppointment = () => {
    if (appointmentStatus === 'confirmed') return false;

    // reminder.allowConfirmAfterCancel
    if (appointmentStatus === 'cancelled' && !settingsReminder.allowConfirmAfterCancel) return false;

    // reminder.replies
    return settingsReminder.replies === 'allowed' || settingsReminder.replies === 'confirmationOnly';
  };

  const canCancelAppointment = () => {
    if (appointmentStatus === 'cancelled') return false;

    // reminder.replies
    if (settingsReminder.replies !== 'allowed') return false;

    // reminder.allowCancelAfterConfirm
    if (appointmentStatus === 'confirmed' && !settingsReminder.allowCancelAfterConfirm) return false;

    // reminder.onConfirmation & reminder.repliesForLastReminder
    if (appointmentStatus === 'confirmed' && settingsReminder.onConfirmation === 'keepLastReminder') {
      return settingsReminder.repliesForLastReminder === 'allowed';
    }

    return true;
  };

  // reminder.allowSelfSchedule & reminder.allowSelfReschedule
  const canRescheduleAppointment = () => {
    if (appointmentStatus === 'cancelled') return false;
    if (settingsReminder.replies !== 'allowed') return false;
    if (
      settingsReminder.onConfirmation === 'keepLastReminder' &&
      settingsReminder.repliesForLastReminder !== 'allowed'
    ) {
      return false;
    }
    return settingsReminder.allowSelfSchedule && settingsReminder.allowSelfReschedule;
  };

  return {
    includeConfirm: canConfirmAppointment(),
    includeCancel: canCancelAppointment(),
    includeReschedule: canRescheduleAppointment(),
  };
}
